import React, { useEffect, useState, memo, useRef, useMemo } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useApi } from "../../../../../../shared/hooks/useApi";
import CustomTable from "../../../../../../shared/components/table/custom-table";
import { LoaderComponent } from "../../../../../../shared/components/loaders/page-loader";
import { fetchFacilityDetails } from './facility-setail-api';
import { useParams } from 'react-router-dom';
import { useScStore } from '../../../../../../store/scStandard.store';

// Row interface: parameters only (extracted from facilityInfo)
interface FacilityRow {
  facilityId: string;
  facilityType?: string;
}

// Props interface for the RenderDetailPanel
interface RenderDetailPanelProps {
  row: { original: { facilityInfo: FacilityRow } };
}

// Column definitions for Facility Information table
const facilityInfoColumns = [
  { accessorKey: 'isMainSite', header: 'Is Facility Main Site', size: 180, minSize: 100, maxSize: 300 },
  { accessorKey: 'facilityNo', header: 'Facility No.', size: 120, minSize: 80, maxSize: 200 },
  { accessorKey: 'facilityType', header: 'Facility Type', size: 120, minSize: 80, maxSize: 200 },
  { accessorKey: 'facilityTypeDescription', header: 'Facility Type Description', size: 220, minSize: 150, maxSize: 400 },
  { accessorKey: 'lastAuditDate', header: 'Last Audit Date', size: 130, minSize: 100, maxSize: 250 },
  { accessorKey: 'farmCapacity', header: 'Farm Capacity', size: 130, minSize: 100, maxSize: 250 },
  { accessorKey: 'iId', header: 'TE-ID', size: 120, minSize: 80, maxSize: 200 },
  { accessorKey: 'facilityStandard', header: 'Facility Standard', size: 160, minSize: 100, maxSize: 300 },
  { accessorKey: 'facilityStandardDescription', header: 'Facility Standard Description', size: 260, minSize: 180, maxSize: 500 },
];

// Column definitions for Address Information table
const addressInfoColumns = [
  { accessorKey: 'name', header: 'Name', size: 240, minSize: 150, maxSize: 500 },
  { accessorKey: 'address1', header: 'Address 1', size: 240, minSize: 150, maxSize: 500 },
  { accessorKey: 'postcode', header: 'Postcode', size: 100, minSize: 80, maxSize: 180 },
  { accessorKey: 'town', header: 'Town', size: 120, minSize: 80, maxSize: 200 },
  { accessorKey: 'countryAreaWithDesc', header: 'Country/Area', size: 160, minSize: 100, maxSize: 300 },
  { accessorKey: 'stateProvinceWithDesc', header: 'State/Province', size: 200, minSize: 120, maxSize: 400 },
];

// Column definitions for Certification Information table
const certificationInfoColumns = [
  { accessorKey: 'subcontractorLicenseNo', header: 'Subcontractor License No.', size: 240, minSize: 150, maxSize: 450 },
  { accessorKey: 'certificationBody', header: 'Certification Body', size: 200, minSize: 120, maxSize: 400 },
];

// Column definitions for Facility Farm Capacity Information table
const facilityFarmInfoColumns = [
  { accessorKey: 'facilityFarmRawMaterialCode', header: 'Farm Raw Material Code', size: 180, minSize: 100, maxSize: 300 },
  { accessorKey: 'facilityFarmRawMaterialCodeDescription', header: 'Farm Raw Material Description', size: 240, minSize: 150, maxSize: 450 },
  { accessorKey: 'facilityFarmRawMaterialCapacity', header: 'Farm Raw Material Capacity', size: 200, minSize: 120, maxSize: 400 },
  { accessorKey: 'facilityFarmRawMaterialNumberOfAnimals', header: 'Facility Farm Raw Material Number Of Animals', size: 300, minSize: 200, maxSize: 500 },
];

// Column definitions for Facility Process Information table
const facilityProcessColumns = [
  { accessorKey: 'facilityProcessCategoryCode', header: 'Process Category Code', size: 180, minSize: 100, maxSize: 300 },
  { accessorKey: 'facilityProcessCategoryCodeDescription', header: 'Process Category Code Description', size: 260, minSize: 150, maxSize: 450 },
  { accessorKey: 'facilityProcessCategoryUserSpecificTerm', header: 'Process User Specific Term', size: 200, minSize: 120, maxSize: 400 },
];

const RenderDetailPanel = memo(({ row }: RenderDetailPanelProps) => {
  // Extract API parameters from row.original.facilityInfo
  const { facilityId, facilityType = '' } = row.original.facilityInfo;
  const { loading, executeGet } = useApi();
  const [facilityData, setFacilityData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);
  const { SC_ID } = useParams();

  const getBorderColor = (type: string) => {
    switch (type) {
      case 'MF': return "#ffb300"; // Warm Gold
      case 'FA': return "#4fc3f7"; // Soft Blue
      case 'CS': return "#81c784"; // Soft Green
      case 'AS': return "#ba68c8"; // Soft Purple
      default: return "#e2e8f0";   // Grey
    }
  };

  const borderColor = getBorderColor(facilityType);

  useEffect(() => {
    if (!SC_ID || !facilityId || hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    fetchFacilityDetails(executeGet, SC_ID, facilityId)
      .then((data) => setFacilityData(data?.data))
      .catch((err) => setError(err.message));
  }, [SC_ID, facilityId, executeGet]);

  if (loading) return <LoaderComponent />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!facilityData) return <Typography>Loading facility details...</Typography>;

  // Use the first facility detail object from API response
  const detail = facilityData || {};

  const mappedFacilityInfo = {
    isMainSite: detail.isMainSite || '',
    facilityNo: detail.facilityNo || '',
    facilityType: detail.facilityType || '',
    facilityTypeDescription: detail.facilityTypeDescription || '',
    facilityOarId: detail.facilityOarId || '',
    lastAuditDate: detail.facilityLastAuditDate || '',
    farmCapacity: Array.isArray(detail.facilityFarmCapacity)
      ? detail.facilityFarmCapacity.join(', ')
      : detail.facilityFarmCapacity || '',
    iId: detail.facilityTeId || '',
    facilityStandard: Array.isArray(detail.facilityStandard)
      ? detail.facilityStandard.join(', ')
      : detail.facilityStandard || '',
    facilityStandardDescription: Array.isArray(detail.facilityStandardDescription)
      ? detail.facilityStandardDescription.join(', ')
      : detail.facilityStandardDescription || '',
  };

  // Map Address Information fields
  const mappedAddressInfo = {
    name: detail.facilityName || '',
    address1: detail.facilityAddress1 || '',
    // address2: detail.facilityAddress2 || '',
    // address3: detail.facilityAddress3 || '',
    postcode: detail.facilityPostcode || '',
    town: detail.facilityTown || '',
    countryArea: detail.facilityCountryOrArea || '',
    countryAreaWithDesc: detail.facilityCountryOrArea ? detail.facilityCountryOrArea + ' - ' +detail.facilityCountryOrAreaDescription : '',
    stateProvince: detail.facilityStateOrProvince || '',
    stateProvinceWithDesc: detail.facilityStateOrProvince ? detail.facilityStateOrProvince + ' - ' + detail.facilityStateOrProvinceDescription : '',
  };

  // Map Certification Information fields
  const mappedCertificationInfo = {
    subcontractorLicenseNo: detail.facilitySubcontractorLicenseNo || '',
    certificationBody: detail.facilityCbOfIndependentlyCertifiedSubcontractor || '',
    // expiryDate: detail.facilityExpiryDateOfIndependentlyCertifiedSubcontractor || '',
  };

  const mappedFacilityFarmCapacityInfo = detail?.facilityFarmCapacityInformation;

  // Map Facility Process Information from detail.facilityProcessDetails array
  const facilityProcess = detail.facilityProcessInformation || [];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 4,
        gap: 4.5,
        width: '100%',
        backgroundColor: '#ffffff', // Clean white background to prevent heavy overpower
        borderRadius: '8px',
        border: `1.5px solid ${borderColor}`,
        boxShadow: `0px 4px 20px ${borderColor}18`, // Elegant subtle shadow tinted with the facility type color
        boxSizing: 'border-box',
        // Global overrides for nested CustomTables to make them ultra-premium and lightweight
        '& .MuiPaper-root': {
          boxShadow: 'none !important',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          overflow: 'hidden',
          backgroundColor: '#ffffff !important',
        },
        '& .MuiTableCell-head': {
          backgroundColor: '#E1ECEB !important', // Premium pastel brand-sage color
          color: '#2C4748 !important', // Deep-teal text
          height: '38px !important',
          py: '4px !important',
          borderBottom: '1px solid #cbd5e1 !important',
        },
        '& .MuiTableCell-head *': {
          color: '#2C4748 !important',
          fontWeight: '600 !important',
          fontSize: '0.75rem !important',
          textTransform: 'uppercase !important',
          letterSpacing: '0.03em !important',
        },
        '& .MuiTableCell-body': {
          py: '8px !important',
          fontSize: '0.8rem !important',
          color: '#475569 !important',
          borderBottom: '1px solid #f1f5f9 !important',
        },
        '& .MuiTableRow-root:last-child .MuiTableCell-body': {
          borderBottom: 'none !important',
        },
        '& .MuiDivider-root': {
          width: '100%',
          borderColor: '#e2e8f0',
          my: 1,
        }
      }}
    >
      {/* Facility Information */}
      <Box sx={{ width: '100%' }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: '#2C4748',
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              display: 'inline-block',
              width: '4px',
              height: '14px',
              backgroundColor: '#2C4748',
              borderRadius: '2px',
              mr: 1,
            }
          }}
        >
          Facility Information
        </Typography>
        <CustomTable
          columns={facilityInfoColumns}
          data={[mappedFacilityInfo]}
          enableTopToolbar={false}
          enableBottomToolbar={false}
          enableColumnFilters={false}
          enableSorting={false}
          enableRowActions={false}
        />
      </Box>
      {/* Address Information */}
      <Box sx={{ width: '100%' }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: '#2C4748',
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              display: 'inline-block',
              width: '4px',
              height: '14px',
              backgroundColor: '#2C4748',
              borderRadius: '2px',
              mr: 1,
            }
          }}
        >
          Address Information
        </Typography>
        <CustomTable
          columns={addressInfoColumns}
          data={[mappedAddressInfo]}
          enableTopToolbar={false}
          enableBottomToolbar={false}
          enableColumnFilters={false}
          enableSorting={false}
          enableRowActions={false}
        />
      </Box>
      {/* Certification Information */}
      <Box sx={{ width: '100%' }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: '#2C4748',
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              display: 'inline-block',
              width: '4px',
              height: '14px',
              backgroundColor: '#2C4748',
              borderRadius: '2px',
              mr: 1,
            }
          }}
        >
          Certification Information
        </Typography>
        {!mappedCertificationInfo.subcontractorLicenseNo && !mappedCertificationInfo.certificationBody ? (
          <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', pl: 1.5, fontSize: '0.8rem' }}>
            This facility is not an independently certified subcontractor (not applicable).
          </Typography>
        ) : (
          <CustomTable
            columns={certificationInfoColumns}
            data={[mappedCertificationInfo]}
            enableTopToolbar={false}
            enableBottomToolbar={false}
            enableColumnFilters={false}
            enableSorting={false}
            enableRowActions={false}
          />
        )}
      </Box>
      {/* Facility Farm Capacity Information */}
      <Box sx={{ width: '100%' }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: '#2C4748',
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              display: 'inline-block',
              width: '4px',
              height: '14px',
              backgroundColor: '#2C4748',
              borderRadius: '2px',
              mr: 1,
            }
          }}
        >
          Facility Farm Capacity Information
        </Typography>
        {!mappedFacilityFarmCapacityInfo || mappedFacilityFarmCapacityInfo.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', pl: 1.5, fontSize: '0.8rem' }}>
            No farm capacity details recorded for this facility.
          </Typography>
        ) : (
          <CustomTable
            columns={facilityFarmInfoColumns}
            data={mappedFacilityFarmCapacityInfo}
            enableTopToolbar={false}
            enableBottomToolbar={false}
            enableColumnFilters={false}
            enableSorting={false}
            enableRowActions={false}
          />
        )}
      </Box>
      {/* Facility Process Information */}
      <Box sx={{ width: '100%' }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            color: '#2C4748',
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '""',
              display: 'inline-block',
              width: '4px',
              height: '14px',
              backgroundColor: '#2C4748',
              borderRadius: '2px',
              mr: 1,
            }
          }}
        >
          Facility Process Information
        </Typography>
        {facilityProcess.length === 0 ? (
          <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', pl: 1.5, fontSize: '0.8rem' }}>
            No process categories assigned to this facility.
          </Typography>
        ) : (
          <CustomTable
            columns={facilityProcessColumns}
            data={facilityProcess}
            enableTopToolbar={false}
            enableBottomToolbar={false}
            enableColumnFilters={false}
            enableSorting={false}
            enableRowActions={false}
          />
        )}
      </Box>
    </Box>
  );
});

export default RenderDetailPanel;
